import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Snackbar,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import MuiAlert, { AlertProps } from "@mui/material/Alert";
import SendIcon from "@mui/icons-material/Send";
import * as Yup from "yup";
import { ValidationError } from "yup";
import { getCustomerLoanRequest, updateCustomerLoanRequest, createCustomerLoanRequest } from "../../../services/customer.service";
import {
  commonMessage,
} from "../../../constants/message.constant";

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const editDealSchema = Yup.object({
  loanName: Yup.string()
    .trim()
    .min(3, "Loan Name must be at least 3 characters")
    .required("Loan Name is required."),
  dealType: Yup.string()
    .oneOf(
      ["purchase", "refinance", "startup"],
      "Please select a valid Deal Type."
    )
    .required("Deal Type is required."),
  loanSize: Yup.number()
    .typeError("Loan Size must be a number.")
    .min(1000, "Loan Size must be at least $1,000.")
    .required("Loan Size is required."),
  maxLTV: Yup.number()
    .typeError("Max LTV must be a number.")
    .min(0, "Max LTV must be 0 or greater.")
    .max(100, "Max LTV cannot exceed 100.")
    .required("Max LTV is required."),
  minDSCRatio: Yup.number()
    .typeError("Min DSC Ratio must be a number.")
    .min(0.5, "Min DSC Ratio should be 0.5 or greater.")
    .required("Min DSC Ratio is required."),
});

interface Props {
  customerId?: string;
  dealId?: string;
  onSuccess?: () => void; // Callback for successful update
}

const EditLoanDeal: React.FC<Props> = ({ customerId = "", dealId, onSuccess }) => {
  const [dealData, setDealData] = useState({
    loanName: "",
    dealType: "",
    loanSize: "",
    maxLTV: "",
    minDSCRatio: "",
  });
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "warning" | "info"
  >("success");

  useEffect(() => {
    if (dealId) {
      setLoading(true);
      getCustomerLoanRequest(dealId)
        .then((response) => {
          if (response) {
            setDealData({
              loanName: response.loanName || "",
              dealType: response.dealType || "",
              loanSize: response.loanSize?.toString() || "",
              maxLTV: response.maxLTV?.toString() || "",
              minDSCRatio: response.minDSCRatio?.toString() || "",
            });
          }
        })
        .catch((error) => {
          showSnackbar("Failed to load deal data", "error");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [dealId]);

  const handleChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | { name?: string; value: unknown }
    >
  ) => {
    const target = event.target as { name: string; value: string | number };
    const { name, value } = target;
    setDealData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSelectChange = (event: {
    target: { name: string; value: unknown };
  }) => {
    const { name, value } = event.target;
    setDealData((prev) => ({ ...prev, [name]: value as string }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const showSnackbar = (
    message: string,
    severity: "success" | "error" | "warning" | "info"
  ) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
    setSnackbarMessage("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors({});
    handleCloseSnackbar();
    setLoading(true);
    try {
      await editDealSchema.validate(dealData, { abortEarly: false });
      if (dealId) {
        await updateCustomerLoanRequest(dealId, dealData);
        showSnackbar("Deal updated successfully", "success");
        if (onSuccess) {
          onSuccess();
        }
      } else if (customerId) {
        await createCustomerLoanRequest(customerId, dealData);
        showSnackbar("Deal created successfully", "success");
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (err: any) {
      if (err instanceof ValidationError) {
        const errors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) {
            errors[error.path] = error.message;
          }
        });
        setFormErrors(errors);
      } else {
        showSnackbar(
          `Update failed: ${err?.message || commonMessage.unknownError}`,
          "error"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const getError = (name: string) => formErrors[name];

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-5" noValidate>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField
            fullWidth
            label="Loan Name"
            name="loanName"
            value={dealData.loanName}
            onChange={handleChange}
            placeholder="e.g., Q4 Inventory Expansion"
            className="bg-white"
            InputProps={{ style: { borderRadius: 10 } }}
            error={!!getError("loanName")}
            helperText={getError("loanName")}
          />
          <FormControl fullWidth size="medium" error={!!getError("dealType")}>
            <InputLabel id="deal-type-label">Deal Type</InputLabel>
            <Select
              labelId="deal-type-label"
              id="dealType"
              name="dealType"
              value={dealData.dealType}
              onChange={handleSelectChange}
              label="Deal Type"
              className="bg-white"
              sx={{ borderRadius: "10px" }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value="purchase">Purchase</MenuItem>
              <MenuItem value="refinance">Refinance</MenuItem>
              <MenuItem value="startup">Startup</MenuItem>
            </Select>
            {!!getError("dealType") && (
              <FormHelperText sx={{ ml: 1.5 }}>
                {getError("dealType")}
              </FormHelperText>
            )}
          </FormControl>
          <TextField
            fullWidth
            label="Loan Size ($)"
            name="loanSize"
            value={dealData.loanSize}
            onChange={handleChange}
            placeholder="e.g., 500,000"
            className="bg-white"
            type="number"
            inputProps={{ min: 0 }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            error={!!getError("loanSize")}
            helperText={getError("loanSize")}
          />
          <TextField
            fullWidth
            label="Max LTV (%)"
            name="maxLTV"
            value={dealData.maxLTV}
            onChange={handleChange}
            placeholder="e.g., 75"
            className="bg-white"
            type="number"
            inputProps={{ min: 0, max: 100 }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            error={!!getError("maxLTV")}
            helperText={getError("maxLTV")}
          />
          <TextField
            fullWidth
            label="Min DSC Ratio"
            name="minDSCRatio"
            value={dealData.minDSCRatio}
            onChange={handleChange}
            placeholder="e.g., 1.25"
            className="bg-white"
            type="number"
            inputProps={{ step: "0.01", min: 0 }}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            error={!!getError("minDSCRatio")}
            helperText={getError("minDSCRatio")}
          />
        </div>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          disabled={loading}
          endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
          className="mt-6 bg-blue-600 hover:bg-blue-700 transition duration-150 ease-in-out"
          sx={{
            borderRadius: 2,
            fontWeight: 600,
          }}
        >
          {dealId ? "Update Deal" : "Create Deal"}
        </Button>
      </form>

      {/* Snackbar Component */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EditLoanDeal;
